export class GeolocationService {
  static async getBrowserGeolocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalização não suportada pelo navegador'));
        return;
      }

      const options = {
        enableHighAccuracy: true, // Alta precisão
        timeout: 10000, // 10 segundos de timeout
        maximumAge: 60000 // Cache de 1 minuto
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          });
        },
        (error) => {
          reject(this.getErrorMessage(error));
        },
        options
      );
    });
  }

  static getErrorMessage(error) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return 'Permissão de localização negada pelo usuário';
      case error.POSITION_UNAVAILABLE:
        return 'Localização indisponível';
      case error.TIMEOUT:
        return 'Tempo limite para obter localização excedido';
      default:
        return 'Erro desconhecido ao obter localização';
    }
  }

  // Método para obter informações adicionais por IP (fallback)
  static async getIpGeolocation() {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) throw new Error('Erro ao obter geolocalização por IP');
      
      return await response.json();
    } catch (error) {
      throw new Error(`Falha na geolocalização por IP: ${error.message}`);
    }
  }

  // Novo método para obter apenas o IP do cliente
  static async getClientIP() {
    try {
      // Tentativa com ipapi.co (já usamos para geolocation)
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) throw new Error('Erro ao obter IP');
      
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.warn('Falha ao obter IP com ipapi.co, tentando método alternativo:', error);
      
      // Fallback 1: ipify.org (serviço simples apenas para IP)
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        if (!response.ok) throw new Error('Erro ao obter IP com fallback');
        
        const data = await response.json();
        return data.ip;
      } catch (fallbackError) {
        console.warn('Falha ao obter IP com ipify.org, tentando último método:', fallbackError);
        
        // Fallback 2: api64.ipify.org
        try {
          const response = await fetch('https://api64.ipify.org?format=json');
          if (!response.ok) throw new Error('Erro ao obter IP com último fallback');
          
          const data = await response.json();
          return data.ip;
        } catch (finalError) {
          throw new Error('Não foi possível obter o endereço IP do cliente');
        }
      }
    }
  }

  // Novo método completo que combina geolocalização + IP
  static async getCompleteLocationData() {
    let locationData = {};
    let ipAddress = null;

    try {
      // 1. Primeiro tenta obter o IP (é mais rápido e não requer permissão)
      ipAddress = await this.getClientIP();
      locationData.ip = ipAddress;
      
      // 2. Tenta obter geolocalização precisa do navegador
      try {
        const browserLocation = await this.getBrowserGeolocation();
        locationData = {
          ...locationData,
          latitude: browserLocation.latitude,
          longitude: browserLocation.longitude,
          accuracy: browserLocation.accuracy,
          source: 'browser',
          timestamp: new Date().toISOString()
        };
        
        // Se conseguiu geolocalização do navegador, retorna imediatamente
        return locationData;
      } catch (browserError) {
        console.warn('Geolocalização do navegador falhou, usando IP:', browserError);
        
        // 3. Fallback para geolocalização por IP
        try {
          const ipLocation = await this.getIpGeolocation();
          locationData = {
            ...locationData,
            latitude: ipLocation.latitude,
            longitude: ipLocation.longitude,
            city: ipLocation.city,
            region: ipLocation.region,
            country: ipLocation.country_name,
            countryCode: ipLocation.country,
            isp: ipLocation.org,
            source: 'ip',
            timestamp: new Date().toISOString()
          };
        } catch (ipError) {
          console.warn('Geolocalização por IP também falhou:', ipError);
          // Mesmo se falhar, pelo menos temos o IP
          locationData.source = 'ip-only';
          locationData.timestamp = new Date().toISOString();
        }
      }
      
      return locationData;
      
    } catch (error) {
      console.error('Erro completo ao obter dados de localização:', error);
      throw new Error('Não foi possível obter dados de localização');
    }
  }

  // Método simplificado apenas para verificação rápida de permissão
  static async checkLocationPermission() {
    try {
      // Tenta obter a localização rapidamente com timeout curto
      await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocalização não suportada'));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          { timeout: 2000, maximumAge: 30000 } // Timeout curto de 2 segundos
        );
      });
      return true;
    } catch (error) {
      return false;
    }
  }
}